import { Period, RRDate } from '@raskrask-dk/raskrask-utils'
import {
    Container,
    DateCarousel,
    Flex,
    Grid,
    Image,
    StarsWithTotal,
    Text,
} from '@raskrask-dk/rr-components'
import { TimeSlot } from '@raskrask-dk/rr-components/lib/Organisms/ProductModal/Tabs/Book/props'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { PartnerBF } from '../../models'
import { useAppSelector } from '../../redux/hooks'
import { getIsMobile } from '../../redux/reducers/appSettings'
import { MapTimeSlot } from '../chemistry/calendarComponents/partnerModal/partnermodal'
import { ItemWithHeader } from '../chemistry/ItemWithHeader'
import {
    CancelBookingModal,
    ICancelBookModalProps,
} from '../chemistry/modifybookComponents/cancelbook.modal'
import { ModifyBookTimeSlotsComponent } from '../chemistry/modifybookComponents/movebook.components'
import { PageTemplate } from '../chemistry/skeleton'
import { MoveBookingTemplateProps } from './props'

export const ModifyBookingTemplate = ({
    dependencies,
    dateHandler,
    timeHandler,
    antiSpam,
    oldBooking,
    buttonsArray,
    cancelBookingHandler,
    flowType,
}: MoveBookingTemplateProps): ReactElement => {
    const { t } = useTranslation()
    const [date, setDate, dates, onScrollEvent] = dateHandler
    const { service, partner } = dependencies ?? {
        partner: undefined,
        service: undefined,
    }
    const {
        numberOfHoursUntillServiceDate,
        setHasCustomerAgreedToCancellationFee,
        shouldPayCancellationFee,
        showCancelBookingModal,
        hasCustomerAgreedToCancellationFee,
        setShowCancelBookingModal,
    } = cancelBookingHandler
    const [time, setTime] = timeHandler
    const isMobile = useAppSelector((state) => getIsMobile(state))
    const canBookingBeMoved = numberOfHoursUntillServiceDate > 24

    const timeSlots: TimeSlot[] = useMemo(() => {
        if (!partner || !date || !service) return []
        const tt = partner.schedule.timeslotsByDay(date, service.duration)
        const ll = tt.map((timeslot: Period) => {
            const timeslotString = timeslot.from.toString('HH:mm')
            return MapTimeSlot(
                {
                    start: timeslotString,
                    end: timeslotString,
                },
                timeslotString === time
            )
        })
        return ll
    }, [partner, date, service, time])

    const carouselComponent = useMemo(() => {
        const onClickDate = (thisDate: string) => {
            !date?.equals(new RRDate(thisDate, 'yyyy-MM-dd'))
                ? setDate(new RRDate(thisDate, 'yyyy-MM-dd'))
                : setDate(undefined)
        }
        return (
            <DateCarousel
                dates={dates}
                datesToShow={isMobile ? 5 : 12}
                onClickDate={onClickDate}
                onScrollEvent={onScrollEvent}
            />
        )
    }, [date, setDate, dates, onScrollEvent, isMobile])

    const timeSlotsComponent = useMemo(() => {
        return ModifyBookTimeSlotsComponent(
            timeSlots,
            time,
            setTime,
            date,
            antiSpam,
            buttonsArray
        )
    }, [time, setTime, date, antiSpam, timeSlots])

    const oldBookingDate = useMemo(
        () => new RRDate(oldBooking?.serviceDate),
        [oldBooking?.serviceDate]
    )
    const oldTime = (
        <Grid>
            <Text size="xl" primary bold>
                {flowType === 'MoveBooking'
                    ? t('movebooking.movetime')
                    : flowType === 'CancelBooking'
                    ? t('cancelbooking.header.text')
                    : ''}
            </Text>
            <Text primary>
                {service
                    ? `${t(`common:servicetypes.${service.id}.info.title`)} ${
                          service.duration.toMillis() / 60 / 1000
                      } min`
                    : ''}
            </Text>
            <Text css={{ textTransform: 'capitalize' }} primary>
                {oldBookingDate.toString('EEEE')}{' '}
                {oldBookingDate.toString('d/M')},{' '}
                {oldBookingDate.toString('HH:mm')}
            </Text>
        </Grid>
    )

    const partnerInfo = partner ? (
        <Flex gap={16} css={{ mt: -50 }}>
            <Image
                height={100}
                src={(partner as PartnerBF).therapistPictureLink}
                css={{ br: '50%' }}
            />
            <Grid gap={4} css={{ alignContent: 'end' }}>
                <Text bold>{partner.alias}</Text>
                <StarsWithTotal
                    numberOfReviews={(partner as PartnerBF).numberOfReviews}
                    rating={(partner as PartnerBF).rating || 0}
                    size="m"
                />
            </Grid>
        </Flex>
    ) : (
        <></>
    )

    const cancelModalProps: ICancelBookModalProps = {
        shouldPayCancellationFee: shouldPayCancellationFee,
        hasCustomerAgreedToCancellationFee: hasCustomerAgreedToCancellationFee,
        setHasCustomerAgreedToCancellationFee:
            setHasCustomerAgreedToCancellationFee,
        numberOfHoursUntillServiceDate: numberOfHoursUntillServiceDate,
        showCancelBookModal: showCancelBookingModal,
        setShowCancelBookingModal: setShowCancelBookingModal,
        onClickCancelBooking: buttonsArray[1].onClickHandler, // argh. volatile. dont like this
    }
    return (
        <>
            {CancelBookingModal(cancelModalProps)}

            <Container css={{ pb: 35 }}>{oldTime}</Container>
            <PageTemplate
                Top={
                    <Grid gap="16">
                        {partnerInfo}
                        <ItemWithHeader
                            header={
                                flowType === 'MoveBooking'
                                    ? t('movebooking.carousel.title')
                                    : flowType === 'CancelBooking' &&
                                      timeSlots.length < 1
                                    ? t(
                                          'cancelbooking.notmoveable.carousel.title'
                                      )
                                    : flowType === 'CancelBooking'
                                    ? t('cancelbooking.carousel.title')
                                    : ''
                            }
                            item={canBookingBeMoved ? carouselComponent : <></>}
                        />
                    </Grid>
                }
                Body={timeSlotsComponent}
                Modal={<></>}
                hideBackButton
            />
        </>
    )
}

export default ModifyBookingTemplate
